import React, { useEffect, useState } from "react";
import { walletPoints, cashWallet } from "../../../../Api/apiUtils";
import { Card, Button, Field, TextInput, Loader, EmptyState } from "../../../../ui";
import { Stat, StatGroup } from "./ui-pending";
import { toast } from "react-toastify";
import "./style.scss";
import "./panels.scss";

interface WalletDetailsProps {
  credentialId: any;
  studentId: any;
  studentData: any;
}

interface WalletData {
  totalArtiumCash: number;
  myCash: number;
  INRPointConversion: number;
  USDPointConversion: number;
}

const WalletDetails: React.FC<WalletDetailsProps> = ({ studentData }) => {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [artiumCashPoints, setArtiumCashPoints] = useState<number>(0);

  const getCurrentDatetime = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const fetchWalletData = async (userId: string, userType: string) => {
    try {
      const response = await walletPoints(userId, userType);
      setWalletData(response);
    } catch (error) {
      setError("Error fetching wallet points");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!studentData?.userId || !studentData?.userType) {
      return;
    }
    fetchWalletData(studentData.userId, studentData.userType);
  }, [studentData]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    const now = new Date();
    const selected = new Date(selectedDate);

    if (!selectedDate || selected >= now) {
      setExpiryDate(selectedDate);
    } else {
      toast.error("Please select a future date and time");
    }
  };

  const handleSubmit = async () => {
    try {
      const userId = studentData?.userId;
      if (!userId) {
        throw new Error("User ID is missing");
      }

      const payload = {
        type: event,
        expiryDate: expiryDate,
        points: artiumCashPoints,
        userId: userId,
      };

      await cashWallet(userId, payload);
      toast.success("Added Artium cash for user");

      setEvent("");
      setExpiryDate("");
      setArtiumCashPoints(0);

      if (studentData?.userId && studentData?.userType) {
        await fetchWalletData(studentData.userId, studentData.userType);
      }
    } catch (error) {
      console.error("Error posting wallet data", error);
      setError("Error submitting wallet data");
    }
  };

  return (
    <div className="wallet-details-page ds-stack">
      <Card title="Wallet details" subtitle="Artium Cash and My Cash balances for this learner.">
        {loading ? (
          <Loader label="Loading wallet balance…" />
        ) : walletData ? (
          <StatGroup>
            <Stat label="Total Artium Cash" value={walletData.totalArtiumCash ?? "N/A"} tone="primary" />
            <Stat label="My Cash" value={walletData.myCash ?? "N/A"} tone="success" />
          </StatGroup>
        ) : (
          <EmptyState
            title={error || "No wallet data available."}
            description="Reopen this section once the learner's profile has loaded."
          />
        )}
      </Card>

      <Card title="Add Artium Cash">
        <div className="profile-panel-form">
          <Field label="Event" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                placeholder="Enter event name"
              />
            )}
          </Field>
          <Field label="Expiry date" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="datetime-local"
                value={expiryDate}
                min={getCurrentDatetime()}
                onChange={handleDateChange}
              />
            )}
          </Field>
          <Field label="Artium Cash points" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                required
                type="number"
                numeric
                value={artiumCashPoints === 0 ? "" : artiumCashPoints}
                onChange={(e) => setArtiumCashPoints(e.target.value === "" ? 0 : Number(e.target.value))}
                placeholder="Enter points"
              />
            )}
          </Field>
          <Button
            variant="primary"
            block
            disabled={!event || !expiryDate || !artiumCashPoints}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default WalletDetails;
